import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Share2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { core } from "@/lib/mh";
import { errText , copyText } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";
import { Panel, Eyebrow } from "@/components/mh/bits";
import { PublicTop } from "@/pages/Audit";

const DEPT_HEX = { sales: "#FF9100", deals: "#FF1744", marketing: "#D500F9", operations: "#00E5FF", intelligence: "#2979FF", customer: "#FF4081", back_office: "#FFD600" };

const Score = ({ v }) => (
  <div className="relative w-36 h-36 flex-shrink-0" data-testid="report-readiness">
    <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90"><circle cx="50" cy="50" r="44" stroke="rgba(255,255,255,0.08)" strokeWidth="6" fill="none" />
      <circle cx="50" cy="50" r="44" stroke="#D4FF3A" strokeWidth="6" fill="none" strokeLinecap="round" strokeDasharray={`${(v / 100) * 276} 276`} className="transition-[stroke-dasharray] duration-1000" /></svg>
    <div className="absolute inset-0 grid place-items-center text-center"><div><div className="font-display text-5xl leading-none">{v}</div><div className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground mt-1">readiness</div></div></div>
  </div>
);

const ApplyBtn = ({ id, applied }) => {
  const { user, canManage } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!user) return nav(`/request-access?audit=${id}`);
    if (!canManage) return toast.error("You need manager access in this workspace");
    setBusy(true);
    try {
      const { data } = await core.applyAudit(id);
      toast.success(`${data.moved_to_in_dev.length} agents moved to In dev · starter workflow created`);
      nav(`/workflows/${data.workflow_id}`);
    } catch (e) {
      toast.error(errText(e));
      setBusy(false);
    }
  };
  return (
    <button onClick={go} disabled={busy} data-testid="report-apply-btn"
      className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#D4FF3A] text-black font-semibold hover:bg-[#e2ff73] transition-colors disabled:opacity-50">
      <Wand2 className="w-4 h-4" /> {user ? (applied ? "Put them into a plan again" : "Put these 10 agents into a plan") : "Log in or request a workspace to plan these"}
    </button>
  );
};

export default function AuditReport() {
  const { id } = useParams();
  const { user } = useAuth();
  const [r, setR] = useState(null);
  useEffect(() => {
    let t;
    const load = () => core.audit(id).then((res) => { setR(res.data); if (res.data.summary_status === "pending") t = setTimeout(load, 2500); }).catch(() => setR(false));
    load();
    return () => clearTimeout(t);
  }, [id]);
  if (r === false) return <div className="min-h-screen grid place-items-center text-muted-foreground" data-testid="report-not-found">Report not found.</div>;
  if (!r) return <div className="min-h-screen grid place-items-center font-mono text-xs text-muted-foreground">Loading report…</div>;
  const share = () => { copyText(window.location.href); toast.success("Share link copied"); };

  return (
    <div className="min-h-screen bg-[#030305]" data-testid="report-page">
      {!user && <PublicTop />}
      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-10 sm:py-16 space-y-10">
        {user && <Link to="/audit" className="font-mono text-xs text-muted-foreground hover:text-foreground" data-testid="report-new-audit">← Take another audit</Link>}
        <header className="flex flex-col sm:flex-row gap-8 sm:items-center mh-rise">
          <Score v={r.readiness} />
          <div className="min-w-0">
            <Eyebrow>Rollout brief · {new Date(r.created_at).toLocaleDateString()}</Eyebrow>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-2 leading-[1.03]">{r.company || "Your company"}: <em className="text-muted-foreground">the first ten.</em></h1>
            <div className="flex flex-wrap gap-2 mt-5">
              <ApplyBtn id={r.id} applied={r.applied} />
              <button onClick={share} className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/15 hover:bg-white/5" data-testid="report-share-btn"><Share2 className="w-4 h-4" /> Share</button>
            </div>
          </div>
        </header>

        {r.summary_status !== "unavailable" && (
          <Panel cls="p-6" testId="report-summary">
            <Eyebrow>Executive summary {r.summary_model ? `· ${r.summary_model.provider}` : ""}</Eyebrow>
            {r.summary ? <p className="font-display text-xl sm:text-2xl leading-snug mt-3">{r.summary}</p> : <p className="text-sm text-muted-foreground mt-3 mh-blink">Writing your summary…</p>}
          </Panel>
        )}

        <section>
          <h2 className="font-display text-3xl mb-4">Top 10 agents to build first</h2>
          <ol className="divide-y divide-white/[0.06] border-y border-white/[0.06]" data-testid="report-top10">
            {r.top10.map((a, i) => (
              <li key={a.slug} className="py-4 grid grid-cols-[36px_1fr] sm:grid-cols-[36px_1fr_auto] gap-x-3 gap-y-1">
                <span className="font-display text-2xl text-muted-foreground">{i + 1}</span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rotate-45" style={{ background: DEPT_HEX[a.department] }} /><span className="font-medium">{a.name}</span><span className="font-mono text-[10px] text-muted-foreground">{a.stage}</span></div>
                  <p className="text-sm text-muted-foreground mt-0.5">{a.one_liner}</p>
                  <p className="text-sm mt-1">{a.reason}</p>
                </div>
                {a.build_hours && <div className="col-start-2 sm:col-start-3 font-mono text-[10px] text-muted-foreground sm:text-right">{a.build_hours.min}–{a.build_hours.max}h build<br />${a.monthly_cost?.min}–{a.monthly_cost?.max}/mo</div>}
              </li>
            ))}
          </ol>
          <p className="font-mono text-[10px] text-muted-foreground mt-2">Build hours and costs are illustrative planning ranges.</p>
        </section>

        <section>
          <h2 className="font-display text-3xl mb-4">Your 90-day plan</h2>
          <div className="grid md:grid-cols-3 gap-3" data-testid="report-plan">
            {r.plan.map((p) => (
              <Panel key={p.days} cls="p-5">
                <Eyebrow>{p.days}</Eyebrow>
                <div className="font-display text-2xl mt-1">{p.title}</div>
                <ul className="mt-3 space-y-1 text-sm">{p.agents.map((a) => <li key={a.slug}>· {a.name}</li>)}</ul>
                <ul className="mt-3 space-y-1 text-xs text-muted-foreground">{p.milestones.map((m) => <li key={m}>— {m}</li>)}</ul>
              </Panel>
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-display text-3xl mb-4">Recommended per department</h2>
          <div className="grid sm:grid-cols-2 gap-3" data-testid="report-departments">
            {r.per_department.map((d) => (
              <div key={d.department} className="rounded-xl border border-white/[0.07] p-4" style={{ borderLeft: `2px solid ${d.hex}` }}>
                <div className="font-display text-xl" style={{ color: d.hex }}>{d.name}</div>
                <ul className="mt-2 space-y-1 text-sm">{d.agents.map((a) => <li key={a.slug} className="flex justify-between gap-3"><span className="truncate">{a.name}</span><span className="font-mono text-[10px] text-muted-foreground">{a.stage}</span></li>)}</ul>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
